/** Authenticated HTTP client for the tenant-owned person directory. */

import type {
  AliasSuggestion,
  ApiEnvelope,
  Person,
  PersonAlias,
  PersonMentionToken,
  ProviderAccountCandidate,
} from '@zunelo/shared';
import { createHttpTransport, type HttpTransport } from './httpTransport';
import { parseEnvelope } from './parseEnvelope';
import type { HttpClientOptions, IPeopleClient } from './types';

export class HttpPeopleClient implements IPeopleClient {
  private readonly transport: HttpTransport;

  constructor(options: HttpClientOptions | string = {}) {
    this.transport = createHttpTransport(options);
  }

  private async request<T>(path: string, init?: RequestInit): Promise<ApiEnvelope<T>> {
    const response = await this.transport.fetch(path, init);
    return parseEnvelope<T>(response, undefined, 'PEOPLE_HTTP_ERROR');
  }

  listPeople(query = ''): Promise<ApiEnvelope<Person[]>> {
    const suffix = query.trim() ? `?q=${encodeURIComponent(query.trim())}` : '';
    return this.request(`/api/people${suffix}`);
  }

  getPerson(personId: string): Promise<ApiEnvelope<Person>> {
    return this.request(`/api/people/${encodeURIComponent(personId)}`);
  }

  createPerson(input: { displayName: string; avatarUrl?: string }): Promise<ApiEnvelope<Person>> {
    return this.request('/api/people', { method: 'POST', body: JSON.stringify(input) });
  }

  updatePerson(
    personId: string,
    input: { displayName?: string; avatarUrl?: string; expectedRevision: number },
  ): Promise<ApiEnvelope<Person>> {
    return this.request(`/api/people/${encodeURIComponent(personId)}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
  }

  attachAlias(
    personId: string,
    alias: ProviderAccountCandidate,
    expectedRevision: number,
  ): Promise<ApiEnvelope<Person>> {
    return this.request(`/api/people/${encodeURIComponent(personId)}/aliases`, {
      method: 'POST',
      body: JSON.stringify({ alias, expectedRevision }),
    });
  }

  detachAlias(
    personId: string,
    aliasId: string,
    expectedRevision: number,
  ): Promise<ApiEnvelope<Person>> {
    return this.request(
      `/api/people/${encodeURIComponent(personId)}/aliases/${encodeURIComponent(aliasId)}`,
      { method: 'DELETE', body: JSON.stringify({ expectedRevision }) },
    );
  }

  setPreferredAlias(
    personId: string,
    platform: PersonAlias['platform'],
    aliasId: string,
    expectedRevision: number,
  ): Promise<ApiEnvelope<Person>> {
    return this.request(
      `/api/people/${encodeURIComponent(personId)}/preferred-aliases/${platform}`,
      { method: 'PUT', body: JSON.stringify({ aliasId, expectedRevision }) },
    );
  }

  deletePerson(
    personId: string,
    expectedRevision: number,
  ): Promise<ApiEnvelope<{ deleted: boolean }>> {
    return this.request(`/api/people/${encodeURIComponent(personId)}`, {
      method: 'DELETE',
      body: JSON.stringify({ expectedRevision }),
    });
  }

  mergePeople(
    targetPersonId: string,
    sourcePersonIds: string[],
    expectedRevision: number,
  ): Promise<ApiEnvelope<Person>> {
    return this.request(`/api/people/${encodeURIComponent(targetPersonId)}/merge`, {
      method: 'POST',
      body: JSON.stringify({ sourcePersonIds, expectedRevision }),
    });
  }

  splitPerson(
    sourcePersonId: string,
    aliasIds: string[],
    expectedRevision: number,
    displayName?: string,
  ): Promise<ApiEnvelope<Person>> {
    return this.request(`/api/people/${encodeURIComponent(sourcePersonId)}/split`, {
      method: 'POST',
      body: JSON.stringify({ aliasIds, expectedRevision, ...(displayName ? { displayName } : {}) }),
    });
  }

  reassignAlias(
    targetPersonId: string,
    aliasId: string,
    sourcePersonId: string,
    expectedRevision: number,
  ): Promise<ApiEnvelope<Person>> {
    return this.request(
      `/api/people/${encodeURIComponent(targetPersonId)}/aliases/${encodeURIComponent(aliasId)}/reassign`,
      { method: 'POST', body: JSON.stringify({ sourcePersonId, expectedRevision }) },
    );
  }

  listSuggestions(): Promise<ApiEnvelope<AliasSuggestion[]>> {
    return this.request('/api/people/suggestions');
  }

  acceptSuggestion(
    suggestionId: string,
    expectedRevision: number,
    candidateExternalId?: string,
  ): Promise<ApiEnvelope<Person>> {
    return this.request(
      `/api/people/suggestions/${encodeURIComponent(suggestionId)}?action=accept`,
      {
        method: 'POST',
        body: JSON.stringify({
          expectedRevision,
          ...(candidateExternalId ? { candidateExternalId } : {}),
        }),
      },
    );
  }

  rejectSuggestion(suggestionId: string): Promise<ApiEnvelope<{ rejected: boolean }>> {
    return this.request(
      `/api/people/suggestions/${encodeURIComponent(suggestionId)}?action=reject`,
      { method: 'POST', body: '{}' },
    );
  }

  resolveMention(token: PersonMentionToken): Promise<ApiEnvelope<PersonAlias>> {
    return this.request('/api/people/resolve/mention', {
      method: 'POST',
      body: JSON.stringify(token),
    });
  }

  exportPeople(): Promise<ApiEnvelope<{ people: Person[] }>> {
    return this.request('/api/people/export');
  }

  searchProviderAccounts(
    platform: PersonAlias['platform'],
    query: string,
  ): Promise<ApiEnvelope<ProviderAccountCandidate[]>> {
    return this.request(
      `/api/people/provider-accounts?platform=${platform}&query=${encodeURIComponent(query)}`,
    );
  }
}
